import React from "react";
import { ShieldCheck, Building2, ReceiptText, Check } from "lucide-react";
import { useLang, UI } from "../i18n";
import { INFOS, THEMES, IMG } from "../content";
import { useLenis, Reveal } from "../components/motion";
import { PageHero } from "../components/PageHero";
import { MiniSite, CourtLines, MonoTag } from "../components/motifs";
import { FaqBlock } from "../components/Fiche";
import { ContactSection } from "../components/Layout";

const Block = ({ icon: Icon, data, testid, color, desc }) => {
  const { t } = useLang();
  return (
    <Reveal>
      <div data-testid={testid} className="h-full rounded-md border border-white/10 p-8"
        style={{ backgroundColor: "var(--ms-surface)", borderTop: `4px solid ${color}` }}>
        <Icon size={30} style={{ color }} />
        <h2 className="font-display text-2xl md:text-3xl uppercase leading-tight mt-5">{t(data.title)}</h2>
        {desc && <p className="font-body text-white/65 mt-4">{t(data.desc)}</p>}
        <ul className="mt-6 space-y-3">
          {data.items.map((it, k) => (
            <li key={k} className="flex gap-3 items-start">
              <span className="mt-1 rounded-full p-0.5" style={{ backgroundColor: color }}>
                <Check size={12} className="text-black" />
              </span>
              <span className="font-body text-sm text-white/85">{t(it)}</span>
            </li>
          ))}
        </ul>
      </div>
    </Reveal>
  );
};

// INFOS PRATIQUES, ACCRÉDITATIONS & CADRE JURIDIQUE
export default function Infos() {
  useLenis();
  const { t } = useLang();
  return (
    <MiniSite theme={THEMES.base}>
      <PageHero
        code="AST · INFOS"
        overline={t(UI.nav.infos)}
        title={t(INFOS.title)}
        subtitle={t(INFOS.intro)}
        image={IMG.coach}
      />
      <section className="relative overflow-hidden pb-24 md:pb-32">
        <CourtLines className="-left-56 bottom-0 w-[640px] h-[640px]" opacity={0.05} />
        <div className="relative max-w-[1400px] mx-auto px-5 md:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Block icon={ShieldCheck} data={INFOS.parents} testid="infos-parents" color="#4C9AFF" />
            <Block icon={Building2} data={INFOS.b2b} testid="infos-b2b" color="#F6B02C" desc />
            <Block icon={ReceiptText} data={INFOS.finance} testid="infos-finance" color="#E5352B" />
          </div>
          <div className="mt-8 rounded-md border border-white/10 px-6 py-5 flex flex-wrap items-center gap-x-8 gap-y-2 font-mono text-[10px] md:text-[11px] uppercase tracking-[0.25em] text-white/60"
            style={{ backgroundColor: "var(--ms-surface)" }} data-testid="infos-legal-strip">
            <MonoTag style={{ color: "#F6B02C" }}>{t({ fr: "Cadre juridique", en: "Legal framework", es: "Marco jurídico" })}</MonoTag>
            <span>{t({ fr: "Mentions légales", en: "Legal notice", es: "Aviso legal" })}</span>
            <span>{t({ fr: "Gestion des risques", en: "Risk management", es: "Gestión de riesgos" })}</span>
            <span>{t({ fr: "Protection des mineurs", en: "Minor protection", es: "Protección de menores" })}</span>
            <span>{t({ fr: "Avoirs valables 2 ans", en: "Credit notes valid 2 years", es: "Abonos válidos 2 años" })}</span>
          </div>
          <div className="mt-8"><FaqBlock accent="#F6B02C" /></div>
        </div>
      </section>
      <ContactSection defaultProgram={t({ fr: "Infos pratiques / B2B", en: "Practical info / B2B", es: "Info práctica / B2B" })} />
    </MiniSite>
  );
}
